import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { MovingTruck } from "@/components/MovingTruck";
import { CaptchaBox, CAPTCHA_ENABLED } from "@/components/CaptchaBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { first_name: "", last_name: "", email: "", phone: "", event_date: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const captchaRef = useRef(null);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    if (CAPTCHA_ENABLED && !token) {
      toast.error("Please complete the CAPTCHA below.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, turnstile_token: token });
      toast.success("Thanks! We can't wait to hear from you — we'll be in touch soon. 🍩");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      captchaRef.current?.reset();
      setToken("");
    }
  };

  const field =
    "w-full rounded-2xl border-2 border-[#e8dcc8] bg-[#f7f0eb] px-4 py-3 text-[#111111] placeholder-[#3d3d3d]/50 focus:outline-none focus:border-[#ff648b] focus:ring-2 focus:ring-[#ff648b]/40 transition";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 px-5 md:px-10 woodgrain">
      <div className="max-w-3xl mx-auto">
        <div className="text-center">
          <MovingTruck />
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#111111] tracking-tight">
            Book the Truck
          </h2>
          <p className="text-lg text-[#3d3d3d] mt-4">
            Want Bobo's at your event, business, or celebration? Fill out the form
            and we'll be in touch shortly. We can't wait to hear from you!
          </p>
        </div>

        <form
          data-testid="booking-form"
          onSubmit={submit}
          className="mt-10 bg-white rounded-3xl p-7 sm:p-10 shadow-xl border-2 border-[#ff648b]/30 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="input-first-name" name="first_name" value={form.first_name} onChange={change} placeholder="First name *" className={field} />
            <input data-testid="input-last-name" name="last_name" value={form.last_name} onChange={change} placeholder="Last name *" className={field} />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="input-email" name="email" type="email" value={form.email} onChange={change} placeholder="Email *" className={field} />
            <input data-testid="input-phone" name="phone" value={form.phone} onChange={change} placeholder="Phone" className={field} />
          </div>
          <input data-testid="input-event-date" name="event_date" value={form.event_date} onChange={change} placeholder="Event date (optional)" className={field} />
          <textarea data-testid="input-message" name="message" value={form.message} onChange={change} placeholder="Tell us about your event *" rows={4} className={field} />

          <CaptchaBox ref={captchaRef} onToken={setToken} />

          <button
            data-testid="booking-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-full bg-[#ff648b] text-[#111111] font-bold text-lg hover:bg-[#ed4a76] hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {loading ? "Sending..." : "Send Booking Request"}
          </button>
        </form>
      </div>
    </section>
  );
};
